﻿import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CONFIG } from '../../core/config';
import { catchError, finalize } from 'rxjs/operators';
import { ExceptionService } from '../../core/services/exception.service';
import { SpinnerService } from '../../core/spinner/spinner.service';

import { CouncilMemberQuery } from '../../core/models/council-members/council-member-query';
import { Question } from '../../core/models/council-members/questions/question';
import { QuestionQuery } from '../../core/models/council-members/questions/question-query';
import { CouncilMemberBasicData } from '../../core/models/council-members/shared/council-member-basic-data';
import { PagedResultModel } from '../../core/models/paged-result.model';
import { CouncilMemberDetails } from '../../core/models/council-members/my-questions/council-member-details';
import { MyQuestion } from '../../core/models/council-members/my-questions/my-question';
import { CurrentConvocationMembers } from '../../core/models/council-members/current-convocation-members';
import { OrganizationalUnitDetails } from '../../core/models/council-members/my-questions/organizational-unit-details';

@Injectable({
    providedIn: 'root'
})
export class CouncilMembersService {

    constructor(private _http: HttpClient,
        private _exceptionService: ExceptionService,
        private _spinnerService: SpinnerService) { }

    searchCouncilMembers(query: CouncilMemberQuery): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.councilMembers.search, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    saveQuestion(data: Question): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.saveQuestion, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getCouncilMemberBasicDataByID(id: number, langID: number): Observable<CouncilMemberBasicData> {
        this._spinnerService.show();
        return this._http.get<CouncilMemberBasicData>(CONFIG.baseUrls.councilMembers.getBasicDataByID + id + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getQuestionsByCouncilMemberID(query: QuestionQuery): Observable<PagedResultModel> {
        let headers = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        this._spinnerService.show();
        return this._http.post<PagedResultModel>(CONFIG.baseUrls.councilMembers.getQuestionsByCouncilMemberID, query, headers)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
  

    getCouncilMemberByUserID(langID: number): Observable<CouncilMemberDetails> {
        this._spinnerService.show();
        return this._http.get<CouncilMemberDetails>(CONFIG.baseUrls.councilMembers.getCouncilMemberByUserID + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    saveCouncilMember(data: FormData): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.saveCouncilMember, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getMyQuestionByID(id: number, langID: number): Observable<MyQuestion> {
        this._spinnerService.show();
        return this._http.get<MyQuestion>(CONFIG.baseUrls.councilMembers.getMyQuestionByID + id + "/" + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    saveMyAnswer(data: MyQuestion): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.saveMyAnswer, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfMemberQuestionsAndAnswers(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getNumberOfMemberQuestionsAndAnswers, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfMemberQuestionsByStatus(langID: number): Observable<any> {
        this._spinnerService.show();
        return this._http.get<any>(CONFIG.baseUrls.councilMembers.getNumberOfMemberQuestionsByStatus + langID.toString())
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getMemberListByAnswerTime(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getMemberListByAnswerTime, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfQuestionsByGender(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getNumberOfQuestionsByGender, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfQuestionsBySensitiveCategory(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getNumberOfQuestionsBySensitiveCategory, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfQuestionsByYearGroup(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getNumberOfQuestionsByYearGroup, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getNumberOfAnsweredAndNotAnsweredQuestions(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getNumberOfAnsweredAndNotAnsweredQuestions, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getMemberListAverageAnswerTime(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getMemberListAverageAnswerTime, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getCouncilMembersForCurrentConvocation(langID: number): Observable<CurrentConvocationMembers[]> {
        this._spinnerService.show();
        return this._http.get<CurrentConvocationMembers[]>(CONFIG.baseUrls.councilMembers.getCouncilMembersForCurrentConvocation + langID)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }

    getCouncilMembersByConvocationID(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getCouncilMembersByConvocationID, data)
            .pipe(
                catchError(this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    GetMemberByMemberTypeId(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.GetMemberByMemberTypeId, data)
            .pipe(
                catchError(() => this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    getMayorByMemberTypeIdAndConvocationId(data: any): Observable<any> {
        this._spinnerService.show();
        return this._http.post<any>(CONFIG.baseUrls.councilMembers.getMayorByMemberTypeIdAndConvocationId, data)
            .pipe(
                catchError(() => this._exceptionService.catchBadResponse),
                finalize(() => this._spinnerService.hide())
            );
    }
    

}